import { RecentApprovalsTicker } from "@/components/site/RecentApprovalsTicker";
import { RefusalNotice, PLAN_ELIGIBILITY } from "@/components/site/RefusalNotice";
import { FirstSessionPromise } from "@/components/site/FirstSessionPromise";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Check,
  X,
  Sparkles,
  Zap,
  Users,
  FileText,
  Brain,
  MessageSquare,
  ClipboardCheck,
  Search,
  AlertTriangle,
  Rocket,
  ArrowRight,
} from "lucide-react";
import { site } from "@/lib/site";
import { useSiteContent } from "@/hooks/useSiteContent";
import { openPlanChat, type PlanKey } from "@/lib/plans";
import { TrustSignals } from "@/components/site/ReviewsTrustBadge";
import { formatInr } from "@/lib/gst";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";


// Approximate conversion - kept as a constant so it's easy to update.
const USD_PER_INR = 1 / 86;
const formatUSD = (inr: number) => "$" + Math.round(inr * USD_PER_INR).toLocaleString("en-US");
const formatINR = (inr: number) => "₹" + inr.toLocaleString("en-IN");

type Currency = "INR" | "USD";

// Primary product image used by Product / Merchant listing structured data.
const PRODUCT_IMAGE = "https://welivdit.com/og-share-v6.jpg";

export const Route = createFileRoute("/pricing")({
  validateSearch: (s: Record<string, unknown>): { enroll?: PlanKey } => ({
    enroll: (s.enroll === "basic" || s.enroll === "full" || s.enroll === "refusal" ? s.enroll : undefined) as
      | PlanKey
      | undefined,
  }),
  head: () => ({
    meta: [
      { title: "US Visa Coaching Pricing - Plans from ₹3,000 | WeLivdIt" },
      {
        name: "description",
        content:
          "Compare US visa interview coaching plans: mock interviews, DS-160 review and refusal recovery. Transparent INR pricing from ₹3,000, no subscriptions.",
      },
      { property: "og:title", content: "US Visa Coaching Pricing - Plans from ₹3,000 | WeLivdIt" },
      {
        property: "og:description",
        content:
          "Compare US visa interview coaching plans: mock interviews, DS-160 review and refusal recovery. Transparent INR pricing from ₹3,000.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://welivdit.com/pricing" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:alt", content: "WeLivdIt - 1,500+ US visa approvals, 4.7 stars on Trustpilot" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "US Visa Coaching Pricing - Plans from ₹3,000 | WeLivdIt" },
      {
        name: "twitter:description",
        content:
          "Compare US visa interview coaching plans: mock interviews, DS-160 review and refusal recovery. Transparent INR pricing from ₹3,000.",
      },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/pricing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Product",
              name: "US Visa Coaching & Mock Interview Plans",
              image: [PRODUCT_IMAGE],
              description:
                "1-on-1 F1 Visa Mock Interviews, DS-160 Profile Analysis, and 214(b) Refusal Recovery coaching.",
              brand: { "@type": "Brand", name: "WeLivdIt" },
              offers: {
                "@type": "AggregateOffer",
                priceCurrency: "INR",
                lowPrice: "3000",
                highPrice: "15000",
                offerCount: "3",
                availability: "https://schema.org/InStock",
                url: "https://welivdit.com/pricing",
              },
            },
            {
              "@type": "Product",
              name: "Basic Support",
              image: [PRODUCT_IMAGE],
              description: "2 one-on-one mock interviews with real-time feedback for confident US visa applicants.",
              brand: { "@type": "Brand", name: "WeLivdIt" },
              offers: {
                "@type": "Offer",
                price: "3000",
                priceCurrency: "INR",
                availability: "https://schema.org/InStock",
                url: "https://welivdit.com/basic-support",
              },
            },
            {
              "@type": "Product",
              name: "Full Support",
              image: [PRODUCT_IMAGE],
              description:
                "End-to-end US visa prep: 4 mocks, DS-160 review, profile analysis, and officer-logic strategy.",
              brand: { "@type": "Brand", name: "WeLivdIt" },
              offers: {
                "@type": "Offer",
                price: "4500",
                priceCurrency: "INR",
                availability: "https://schema.org/InStock",
                url: "https://welivdit.com/full-support",
              },
            },
            {
              "@type": "Product",
              name: "Refusal Support",
              image: [PRODUCT_IMAGE],
              description:
                "214(b) refusal coaching: root-cause analysis, DS-160 review, red-flag fixes, and 5 mocks for a re-attempt.",
              brand: { "@type": "Brand", name: "WeLivdIt" },
              offers: {
                "@type": "Offer",
                price: "7000",
                priceCurrency: "INR",
                availability: "https://schema.org/InStock",
                url: "https://welivdit.com/refusal-support",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: PricingPage,
});

const features = [
  { icon: Users, label: "One-One Mocks & Feedback", values: ["2", "4", "5"] },
  { icon: FileText, label: "Documentation Assistance", values: [true, true, true] },
  { icon: Brain, label: "Visa Officer Logic & Response Strategy", values: [false, "1", "1"] },
  { icon: MessageSquare, label: "Spontaneous Question Training", values: [false, true, true] },
  { icon: ClipboardCheck, label: "DS-160 Form Guidance", values: [false, true, true] },
  { icon: Search, label: "Profile Analysis", values: [false, true, true] },
  { icon: AlertTriangle, label: "Refusal Analysis & Root Cause", values: [false, false, true] },
  { icon: Rocket, label: "Red Flags & Re-attempt Prep", values: [false, false, true] },
];

// TEMP: testing prices - revert before going live (3000 / 4500 / 7000)
type PlanBadge = { text: string; tone: "primary" | "warning" | "muted" };

const basePlans: Array<{
  key: PlanKey;
  name: string;
  inr: number;
  tag: string;
  accent: string;
  learnTo: "/basic-support" | "/full-support" | "/refusal-support";
  highlight?: boolean;
  badge?: PlanBadge;
}> = [
  {
    key: "basic",
    name: "Basic Support",
    inr: 3000,
    tag: "For confident applicants who just need practice",
    accent: "bg-sky-50 text-sky-900 border-sky-200",
    learnTo: "/basic-support",
    badge: { text: "🛠️ LIGHT PRACTICE (Best for final polish)", tone: "muted" },
  },
  {
    key: "full",
    name: "Full Support",
    inr: 4500,
    tag: "For first-timers who want end-to-end guidance",
    accent: "bg-orange-50 text-orange-900 border-orange-300",
    learnTo: "/full-support",
    highlight: true,
    badge: { text: "🔥 RECOMMENDED (Best for First-Time Applicants)", tone: "primary" },
  },
  {
    key: "refusal",
    name: "Refusal Support",
    inr: 7000,
    tag: "After a 214(b) refusal - full re-attempt prep",
    accent: "bg-amber-50 text-amber-900 border-amber-200",
    learnTo: "/refusal-support",
    badge: { text: "🚨 SPECIALIST TRACK (Required if you have a 214(b) stamp)", tone: "warning" },
  },
];

const basePlanFor = [
  {
    name: "Basic Support",
    who: "You're already strong on your profile and answers - you just need a couple of structured mocks to sharpen delivery and confidence.",
  },
  {
    name: "Full Support",
    who: "First-time applicant or unsure where you stand. You want DS-160 review, profile analysis, multiple mocks, and a clear officer-logic strategy from start to interview day.",
  },
  {
    name: "Refusal Support",
    who: "You were refused (214(b) or otherwise). You need a root-cause analysis, red-flag fixes, and a complete re-attempt plan so the second interview lands.",
  },
];

function CurrencyToggle({ value, onChange }: { value: Currency; onChange: (c: Currency) => void }) {
  return (
    <div className="inline-flex items-center rounded-full border border-border bg-card p-1 text-sm shadow-soft">
      {(["INR", "USD"] as Currency[]).map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={
            "px-4 py-1.5 rounded-full transition font-medium " +
            (value === c ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")
          }
          aria-pressed={value === c}
        >
          {c === "INR" ? "₹ INR" : "$ USD"}
        </button>
      ))}
    </div>
  );
}

function Cell({ value }: { value: boolean | string; highlight?: boolean }) {
  if (value === true) {
    return <Check className="h-5 w-5 mx-auto text-primary" />;
  }
  if (value === false) {
    return <X className="h-5 w-5 mx-auto text-muted-foreground" aria-label="Not included" />;
  }
  return <span className="font-display text-lg font-semibold text-primary">{value}</span>;
}


function PricingPage() {
  const { t } = useSiteContent();
  const plans = basePlans.map((p) => ({
    ...p,
    tag: t(`plan.${p.key}.tag`),
    ...(p.badge ? { badge: { ...p.badge, text: t(`plan.${p.key}.badge`) } } : {}),
  }));
  const planFor = basePlanFor.map((p, i) => ({
    ...p,
    who: t(`plan.${(["basic", "full", "refusal"] as const)[i]}.who`),
  }));
  const { enroll } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [currency, setCurrency] = useState<Currency>("INR");
  const [userTouched, setUserTouched] = useState(false);
  const formatPrice = (inr: number) => (currency === "INR" ? formatINR(inr) : formatUSD(inr));

  const [orderPlanKey, setOrderPlanKey] = useState<PlanKey | null>(null);
  const orderPlan = orderPlanKey ? basePlans.find((p) => p.key === orderPlanKey)! : null;
  const openOrderSummary = (key: PlanKey) => setOrderPlanKey(key);
  const closeOrderSummary = () => setOrderPlanKey(null);
  const proceedToPay = () => {
    if (orderPlanKey) {
      closeOrderSummary();
      openPlanChat(orderPlanKey);
    }
  };

  // Auto-open the enrollment chatbot when arriving with ?enroll=<plan>
  // (used after sign-in to resume the flow).
  useEffect(() => {
    if (!enroll) return;
    openPlanChat(enroll);
    navigate({ to: "/pricing", search: {}, replace: true });
  }, [enroll, navigate]);

  // Auto-detect visitor country and default to USD for non-Indian visitors.
  // Skips if the user has already toggled manually.
  useEffect(() => {
    if (userTouched) return;
    const cached = typeof window !== "undefined" ? sessionStorage.getItem("welivdit:country") : null;
    const apply = (country: string | null) => {
      if (country && country !== "IN") setCurrency("USD");
    };
    if (cached !== null) {
      apply(cached);
      return;
    }
    const controller = new AbortController();
    fetch("https://ipapi.co/json/", { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const country = data?.country_code ?? data?.country ?? null;
        if (country) sessionStorage.setItem("welivdit:country", country);
        apply(country);
      })
      .catch(() => {
        // Silent fail - keep INR default if geolocation is blocked or offline.
      });
    return () => controller.abort();
  }, [userTouched]);

  const handleCurrencyChange = (c: Currency) => {
    setUserTouched(true);
    setCurrency(c);
  };

  return (
    <>
      <section id="pricing" className="bg-gradient-hero scroll-mt-24 md:scroll-mt-28">
        <div className="mx-auto max-w-4xl px-5 pt-10 pb-6 md:pt-24 md:pb-10 text-center flex flex-col">
          {/* Top content: headline + CTA + price summary */}
          <div className="order-1">
            <p className="text-sm font-medium text-primary uppercase tracking-wider">Pricing</p>
            <h1 className="mt-2 md:mt-3 font-display text-4xl md:text-6xl font-semibold tracking-tight text-balance">
              Premium Visa Prep Plans
            </h1>
            <p className="mt-3 md:mt-5 text-base md:text-lg text-foreground/70">
              Pick the support level that fits your timeline. Every plan is hands-on with a real Specialist.
            </p>
            <div className="mt-3 md:mt-4">
              <Link
                to="/find-your-plan"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-foreground/20 text-foreground text-sm font-medium hover:bg-foreground/5 transition"
              >
                <Sparkles className="h-4 w-4 text-primary" /> Not sure which plan? Take the 60-sec finder
              </Link>
            </div>
            <p className="mt-3 md:mt-4 text-sm md:text-base text-foreground/80">
              <strong>Basic Support: ₹3,000</strong> &nbsp;·&nbsp;
              <strong>Full Support: ₹4,500</strong> &nbsp;·&nbsp;
              <strong>Refusal Support: ₹7,000</strong>
            </p>

            <FirstSessionPromise className="mt-5" />
          </div>

          {/* Mobile: plan cards render right after hero headline/CTA */}
          <div className="order-2 md:hidden py-6">
            <h2 className="mb-5 font-display text-2xl font-semibold tracking-tight">Choose your coaching plan</h2>
            <RecentApprovalsTicker className="-mx-5 mb-4" />
        <RefusalNotice className="mb-5" />
            <div className="grid gap-6">
              {plans.map((p, planIdx) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => openOrderSummary(p.key)}
                  className={
                    "group relative rounded-3xl border p-7 block text-left transition-all " +
                    (p.highlight
                      ? "bg-gradient-to-br from-sky-50 via-white to-indigo-50 text-foreground border-2 border-indigo-900/30 ring-4 ring-indigo-900/10 shadow-2xl shadow-indigo-900/25 mt-4 overflow-visible"
                      : "bg-card border-border hover:shadow-soft hover:-translate-y-1 overflow-hidden")
                  }
                >
                  {p.badge && (
                    <div
                      className={
                        "mb-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider " +
                        (p.badge.tone === "warning"
                          ? "bg-red-600 text-white border border-red-700"
                          : p.badge.tone === "muted"
                            ? "bg-muted text-foreground/70 border border-border"
                            : "bg-gradient-cta text-primary-foreground border-2 border-primary/60 animate-pulse")
                      }
                    >
                      {p.badge.text}
                    </div>
                  )}
                  <div className="font-display text-xl font-semibold">{p.name}</div>
                  <div className="text-sm mt-1 text-muted-foreground">{p.tag}</div>
                  <div className="mt-2 text-xs font-semibold text-amber-700 dark:text-amber-300">{PLAN_ELIGIBILITY[p.key]}</div>
                  <ul className="mt-6 space-y-3">
                    {features.map((f) => {
                      const v = f.values[planIdx];
                      return (
                        <li key={f.label} className="flex items-center gap-3 text-sm">
                          <f.icon
                            className={"h-4 w-4 shrink-0 " + (p.highlight ? "text-primary" : "text-muted-foreground")}
                          />
                          <span className="flex-1 text-foreground/80">{f.label}</span>
                          <span className="w-8 text-center">
                            <Cell value={v} highlight={p.highlight} />
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="mt-7 text-center">
                    <div className="font-display text-4xl font-bold tracking-tight">{formatPrice(p.inr)}</div>


                    <div
                      className={
                        "mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold transition " +
                        (p.highlight
                          ? "bg-gradient-cta text-primary-foreground shadow-glow"
                          : "bg-foreground text-background")
                      }
                    >
                      Get {p.name.replace(" Support", "")} Support{" "}
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Bottom content: badge, currency, trust signals */}
          <div className="order-3">
            <p className="mt-2 text-xs text-muted-foreground">
              Prices in Indian Rupees (INR). One-time payment per plan.
            </p>
            <div className="mt-4 md:mt-7 flex flex-col items-center gap-2">
              <CurrencyToggle value={currency} onChange={handleCurrencyChange} />
              {currency === "USD" && (
                <p className="text-xs text-muted-foreground">
                  Approximate conversion for international students. Billed in INR.
                </p>
              )}
            </div>
            <h2 className="sr-only">Why students trust WeLivdIt</h2>
            <TrustSignals />
          </div>
        </div>
      </section>

      {/* Desktop: comparison table */}
      <section className="mx-auto max-w-6xl px-5 pt-20 pb-16 hidden md:block">
        <h2 className="mb-8 text-center font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          Compare what each plan includes
        </h2>
        <RecentApprovalsTicker className="-mx-5 mb-4" />
        <RefusalNotice className="mb-5" />
        <div className="rounded-3xl border border-border bg-card overflow-hidden shadow-soft">
          <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr]">
            {/* Header row */}
            <div className="p-6 bg-muted/40 border-b border-border">
              <div className="font-display text-lg font-semibold">Pricing</div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">Feature</div>
            </div>
            {plans.map((p) => (
              <button
                key={p.name}
                type="button"
                onClick={() => openOrderSummary(p.key)}
                className={
                  "relative block p-6 pt-4 border-b border-border text-center transition hover:opacity-90 text-left w-full " + p.accent
                }
              >
                {p.badge && (
                  <div
                    className={
                      "mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider leading-tight max-w-full " +
                      (p.badge.tone === "warning"
                        ? "bg-red-600 text-white border border-red-700"
                        : p.badge.tone === "muted"
                          ? "bg-muted text-foreground/70 border border-border"
                          : "bg-gradient-cta text-primary-foreground border-2 border-primary/60 animate-pulse")
                    }
                  >
                    <span className="whitespace-normal">{p.badge.text}</span>
                  </div>
                )}
                <div className="font-display text-3xl font-semibold tracking-tight">{formatPrice(p.inr)}</div>


                <div className="mt-2 text-xs uppercase tracking-wider font-semibold opacity-80">{p.name}</div>
                <div className="mt-1.5 text-[11px] font-semibold leading-snug text-amber-700 dark:text-amber-300">{PLAN_ELIGIBILITY[p.key]}</div>
              </button>
            ))}

            {/* Feature rows */}
            {features.map((f, idx) => (
              <div key={f.label} className="contents">
                <div
                  className={"flex items-center gap-3 px-6 py-4 " + (idx % 2 === 0 ? "bg-background" : "bg-muted/20")}
                >
                  <f.icon className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm font-medium text-foreground/85">{f.label}</span>
                </div>
                {f.values.map((v, i) => (
                  <div
                    key={i}
                    className={
                      "flex items-center justify-center px-4 py-4 " + (idx % 2 === 0 ? "bg-background" : "bg-muted/20")
                    }
                  >
                    <Cell value={v} />
                  </div>
                ))}
              </div>
            ))}

            {/* Enroll row */}
            <div className="px-6 py-6 bg-muted/40" />
            {plans.map((p) => (
              <button
                key={p.name}
                type="button"
                onClick={() => openOrderSummary(p.key)}
                className="block px-4 py-6 bg-muted/40 text-center transition hover:opacity-90 text-left w-full"
              >
                <div className="font-display text-3xl font-bold tracking-tight mb-3">{formatPrice(p.inr)}</div>

                <div
                  className={
                    "inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full font-semibold " +
                    (p.highlight
                      ? "bg-gradient-cta text-primary-foreground shadow-glow"
                      : "bg-foreground text-background")
                  }
                >
                  Get {p.name.replace(" Support", "")} Support <ArrowRight className="h-4 w-4" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* Onboarding timeline - what happens right after enrollment */}
      <section className="mx-auto max-w-6xl px-5 pt-4 pb-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">Onboarding</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            What happens the moment you secure your prep?
          </h2>
        </div>
        <ol className="mt-12 grid gap-6 md:grid-cols-3 md:gap-8 relative">
          {[
            {
              n: 1,
              icon: "📧",
              title: "Dashboard Access",
              body: "Instantly receive your automated profile workspace and scheduling dashboard via email.",
            },
            {
              n: 2,
              icon: "📅",
              title: "Book Your Dates",
              body: "Lock in mock interview sessions that directly align with your upcoming consulate timeline.",
            },
            {
              n: 3,
              icon: "🧠",
              title: "Get Grilled",
              body: "Step into 1-on-1, officer-style hot seat drills until your answers are flawless.",
            },
          ].map((step, i, arr) => (
            <li key={step.n} className="relative rounded-3xl border border-border bg-card p-7 shadow-soft/50">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-10 w-10 rounded-full bg-gradient-cta text-primary-foreground font-bold shadow-glow">
                  {step.n}
                </span>
                <span className="text-3xl leading-none" aria-hidden>
                  {step.icon}
                </span>
              </div>
              <div className="mt-4 font-display text-xl font-semibold">{step.title}</div>
              <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{step.body}</p>
              {i < arr.length - 1 && (
                <ArrowRight
                  className="hidden md:block absolute top-1/2 -right-5 -translate-y-1/2 h-6 w-6 text-primary/60"
                  aria-hidden
                />
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* Who is each plan for */}
      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">Not sure which to pick?</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            Which plan is right for you?
          </h2>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {planFor.map((p) => (
            <div key={p.name} className="rounded-3xl border border-border bg-card p-7 shadow-soft/50">
              <div className="font-display text-xl font-semibold">{p.name}</div>
              <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{p.who}</p>
            </div>
          ))}
        </div>
      </section>


      {/* Order Summary modal */}
      {orderPlan && (
        <Dialog open onOpenChange={closeOrderSummary}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Order Summary</DialogTitle>
              <DialogDescription>Review your plan and total before proceeding to secure checkout.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{orderPlan.name}</span>
                <span className="font-medium">{formatInr(orderPlan.inr)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-3">
                <span className="font-semibold">Total amount charged</span>
                <span className="font-display text-2xl font-bold tracking-tight">{formatInr(orderPlan.inr)}</span>
              </div>
              <p className="text-xs text-muted-foreground">Final price in INR. Charged securely via Razorpay.</p>
            </div>
            <Button
              onClick={proceedToPay}
              className="w-full rounded-full h-11 bg-gradient-cta text-primary-foreground font-semibold"
            >
              Proceed to Pay {formatInr(orderPlan.inr)}
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
